import { z } from 'zod';
import { characterSchema, clueSchema, cursesBlockSchema, encounterSchema, itemSchema, monsterSchema, npcSchema, pageSchema, roomsBlockSchema, } from './content.js';
import { BUILTIN_KINDS, kindScopeSchema, legacyTypeForKind, normalizeKindId } from './kinds.js';
import { slugify } from './utils.js';
/**
 * The built-in section types the binder knows how to render. `page` is a custom
 * GM-authored markdown section; `custom` is a forward-compatible escape hatch.
 */
export const sectionTypeSchema = z.enum([
    'overview',
    'rooms',
    'characters',
    'npcs',
    'clues',
    'items',
    'bestiary',
    'encounters',
    'initiative',
    'notes',
    'curses',
    'gallery',
    'page',
    'custom',
]);
/**
 * A section is a first-class, data-driven entry in the binder. The left-hand nav and
 * routing are built from the enabled sections, ordered by `order`.
 *
 * `kind` is the canonical declarative reference (built-in kind id or a custom kind id from
 * the manifest `kinds:` block). `type` is the legacy field still consumed by the current
 * runtime renderer; {@link normalizeAdventure} keeps it in sync with `kind`.
 */
export const sectionSchema = z.object({
    id: z.string(),
    /** Canonical kind id (place, character, beast, … or a custom kind id). */
    kind: z.string().optional(),
    /** Legacy renderer hint, derived from `kind`. Kept for back-compat. */
    type: sectionTypeSchema.default('custom'),
    title: z.string(),
    icon: z.string().default('📄'),
    /** adventure (rendered top) vs system (rendered bottom). */
    scope: kindScopeSchema.optional(),
    /** Locked from delete/retype (the 6 fixed adventure kinds + system kinds). */
    fixed: z.boolean().optional(),
    enabled: z.boolean().default(true),
    order: z.number().default(0),
    /** For `page`/`custom` sections: the id of the backing markdown page. */
    pageId: z.string().optional(),
});
/** High-level descriptive metadata shown on the adventure card and overview. */
export const adventureMetaSchema = z
    .object({
    level: z.union([z.string(), z.number()]).optional(),
    playerCount: z.string().optional(),
    duration: z.string().optional(),
    setting: z.string().optional(),
    tone: z.string().optional(),
    tagline: z.string().optional(),
})
    .catchall(z.unknown());
/** A free-form, GM-titled overview block (replaces the fixed prologue/hook/... keys). */
export const overviewBlockSchema = z.object({
    id: z.string(),
    title: z.string().default(''),
    body: z.string().default(''),
    icon: z.string().optional(),
});
/** The narrative "overview" block (formerly adventure.json top-level prose fields). */
export const overviewSchema = z
    .object({
    prologue: z.string().optional(),
    hookExtra: z.string().optional(),
    victory: z.string().optional(),
    deathRule: z.string().optional(),
    twistFinale: z.string().optional(),
    credits: z.string().optional(),
    /** GM-authored, freely-titled sections. When present these supersede the legacy keys. */
    sections: z.array(overviewBlockSchema).optional(),
})
    .catchall(z.unknown());
/**
 * The full adventure template. This is the canonical, editable one-shot. Running it
 * creates an Instance (see instance.ts) which holds all mutable session state.
 */
export const adventureSchema = z.object({
    /**
     * Schema version. v2 = single `adventure.json`; v3 = folder of Markdown files with the
     * declarative kind/section system. Both validate; {@link normalizeAdventure} upgrades to 3.
     */
    schemaVersion: z.union([z.literal(2), z.literal(3)]).default(3),
    slug: z.string(),
    title: z.string(),
    subtitle: z.string().default(''),
    cover: z.string().default(''),
    meta: adventureMetaSchema.default({}),
    overview: overviewSchema.default({}),
    /** Custom (GM-authored) entity-type definitions, keyed by kind id (manifest `kinds:`). */
    kinds: z.record(z.unknown()).default({}),
    sections: z.array(sectionSchema).default([]),
    rooms: roomsBlockSchema.default({ floors: [], rooms: [] }),
    characters: z.array(characterSchema).default([]),
    npcs: z.array(npcSchema).default([]),
    clues: z.array(clueSchema).default([]),
    items: z.array(itemSchema).default([]),
    monsters: z.array(monsterSchema).default([]),
    encounters: z.array(encounterSchema).default([]),
    curses: cursesBlockSchema.optional(),
    /** Custom markdown sections, referenced by `section.pageId`. */
    pages: z.array(pageSchema).default([]),
    /** Adventure-level DM notes (the `notes` system section; per-run notes live in the instance). */
    authorNotes: z.string().default(''),
    /** Entities for custom (non-built-in) kinds, keyed by kind id. Built-in kinds use their named buckets. */
    entities: z.record(z.array(z.record(z.unknown()))).default({}),
    /** Map of "Display Name" -> image path, for character/entity portraits. */
    images: z.record(z.string()).default({}),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
});
/** Lightweight summary used by the Library list (no heavy content). */
export const adventureSummarySchema = z.object({
    slug: z.string(),
    title: z.string(),
    subtitle: z.string().default(''),
    cover: z.string().default(''),
    meta: adventureMetaSchema.default({}),
    updatedAt: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------
/**
 * The list bucket + body (role:body) field for each built-in entity kind. Used by both
 * {@link normalizeAdventure} and the Markdown (de)serializer so the two stay in lockstep.
 */
export const KIND_BUCKETS = [
    { kind: 'place', bucket: 'rooms', bodyField: 'description' }, // bucket handled specially (rooms.rooms)
    { kind: 'character', bucket: 'characters', bodyField: 'backstory' },
    { kind: 'npc', bucket: 'npcs', bodyField: 'description' },
    { kind: 'beast', bucket: 'monsters', bodyField: 'notes' },
    { kind: 'encounter', bucket: 'encounters', bodyField: 'notes' },
    { kind: 'clue', bucket: 'clues', bodyField: 'text' },
    { kind: 'item', bucket: 'items', bodyField: 'description' },
];
const BODY_FIELD_BY_BUCKET = Object.fromEntries(KIND_BUCKETS.map((b) => [b.bucket, b.bodyField]));
/** Trim + stringify a prose value so body fields round-trip identically. */
function coerceBody(value) {
    return (value == null ? '' : String(value)).trim();
}
/** Assign stable, unique ids to a bucket of entities (id ← existing or slugify(name)). */
function assignIds(list, nameKey) {
    const seen = new Set();
    for (const e of list) {
        let id = (typeof e.id === 'string' && e.id) || slugify(String(e[nameKey] ?? '')) || 'entry';
        let candidate = id;
        let n = 2;
        while (seen.has(candidate)) {
            candidate = `${id}-${n}`;
            n += 1;
        }
        seen.add(candidate);
        e.id = candidate;
    }
}
/**
 * Bring an adventure to the canonical in-memory v3 form. Idempotent.
 *
 * - validates + applies schema defaults
 * - bumps schemaVersion to 3
 * - assigns stable namespaced ids (characters ← slugify(name); fills missing entity ids)
 * - derives each section's canonical `kind`, legacy `type`, `scope` and `fixed`
 * - coerces every entity's body (role:body) field to a trimmed string
 *
 * The round-trip guarantee is defined against this function:
 * `parse(serialize(normalize(adv)))` deep-equals `normalize(adv)`.
 */
export function normalizeAdventure(input) {
    const adv = adventureSchema.parse(input);
    adv.schemaVersion = 3;
    // Stable ids for buckets whose entities may lack them.
    assignIds(adv.characters, 'name');
    assignIds(adv.npcs, 'name');
    assignIds(adv.monsters, 'name');
    assignIds(adv.items, 'name');
    assignIds(adv.clues, 'title');
    assignIds(adv.encounters, 'name');
    for (const [kind, list] of Object.entries(adv.entities)) {
        assignIds(list, 'name');
        void kind;
    }
    // Coerce body fields so they round-trip through the Markdown body.
    for (const { bucket, bodyField } of KIND_BUCKETS) {
        if (bucket === 'rooms') {
            for (const r of adv.rooms.rooms)
                r[bodyField] = coerceBody(r[bodyField]);
        }
        else {
            for (const e of adv[bucket])
                e[bodyField] = coerceBody(e[bodyField]);
        }
    }
    // Place-specific cleanup so fence + link fields round-trip exactly.
    for (const r of adv.rooms.rooms) {
        for (const key of ['readAloud', 'event']) {
            if (typeof r[key] === 'string') {
                const t = r[key].trim();
                if (t)
                    r[key] = t;
                else
                    delete r[key];
            }
        }
        if (Array.isArray(r.details)) {
            const cleaned = r.details.map((d) => String(d).trim()).filter(Boolean);
            if (cleaned.length)
                r.details = cleaned;
            else
                delete r.details;
        }
        for (const key of ['npcs', 'items', 'clues', 'connections']) {
            if (Array.isArray(r[key]) && r[key].length === 0)
                delete r[key];
        }
    }
    // Derive section kind/type/scope/fixed.
    for (const s of adv.sections) {
        const kind = normalizeKindId(s.kind ?? s.type ?? 'custom');
        s.kind = kind;
        s.type = legacyTypeForKind(kind);
        const def = resolveKindLocal(kind, adv.kinds);
        if (s.scope === undefined)
            s.scope = def?.scope ?? 'adventure';
        if (s.fixed === undefined && def?.fixed)
            s.fixed = true;
    }
    ensureSystemSections(adv);
    return adv;
}
/** System kinds every adventure should expose, in their bottom-of-nav order. */
const SYSTEM_SECTION_KINDS = ['notes', 'gallery', 'music', 'compendium'];
/**
 * Guarantees the locked system sections (notes/gallery/music/compendium) exist on every
 * adventure, appended after the existing sections so they render at the bottom of the
 * binder nav and section manager. Idempotent: only missing kinds are added.
 */
function ensureSystemSections(adv) {
    const present = new Set(adv.sections.map((s) => normalizeKindId(s.kind ?? s.type ?? 'custom')));
    const usedIds = new Set(adv.sections.map((s) => s.id));
    let maxOrder = adv.sections.reduce((m, s) => Math.max(m, s.order ?? 0), -1);
    for (const kind of SYSTEM_SECTION_KINDS) {
        if (present.has(kind))
            continue;
        const def = BUILTIN_KINDS[kind];
        if (!def)
            continue;
        let id = kind;
        let n = 2;
        while (usedIds.has(id))
            id = `${kind}-${n++}`;
        usedIds.add(id);
        adv.sections.push({
            id,
            kind,
            type: legacyTypeForKind(kind),
            title: def.label,
            icon: def.icon,
            scope: 'system',
            fixed: true,
            enabled: true,
            order: ++maxOrder,
        });
    }
}
/** Lightweight kind lookup that reads scope/fixed from custom kinds or the built-in registry. */
function resolveKindLocal(kind, customKinds) {
    const custom = customKinds?.[kind];
    if (custom)
        return custom;
    return BUILTIN_KINDS[kind];
}
export { BODY_FIELD_BY_BUCKET };
//# sourceMappingURL=adventure.js.map